/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-nested-ternary */
import { memo, useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  CircularProgress,
  InputBase,
  MenuItem,
  Chip,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import moment from 'moment';
import { Iconify } from 'src/components';
import UserSearchDropdown from 'src/components/UserSearchDropdown';
import { useSelector, dispatch } from 'src/redux/store';
import { get10WpTemplateList, getSearchWpTemplateList } from 'src/redux/slices/whatsAppMessages';
import { getTemplateList } from 'src/redux/slices/practiceMessage';
import { getWhatsappAccountPhones, whatsappMessageSend } from 'src/redux/slices/whatsappAccount';
import { getFirstConnectedWhatsappBusinessAccount } from 'src/utils/whatsappAccountLifecycle';

// Design tokens matching AISummaryModal
const colors = {
  primary: { main: '#00A76F', light: '#5BE49B', lighter: '#C8FAD6', dark: '#007867' },
  secondary: { main: '#8E33FF', lighter: '#EFD6FF' },
  success: { main: '#22C55E', light: '#77ED8B', lighter: '#D3FCD2', dark: '#118D57' },
  warning: { main: '#FFAB00', light: '#FFD666', lighter: '#FFF5CC', dark: '#B76E00' },
  error: { main: '#FF5630', light: '#FFAC82', lighter: '#FFE9D5', dark: '#B71D18' },
  info: { main: '#00B8D9', light: '#61F3F3', lighter: '#CAFDF5', dark: '#006C9C' },
  grey: { 100: '#F4F6F8', 200: '#DFE3E8', 300: '#C4CDD5', 500: '#637381', 700: '#212B36' },
  whatsapp: { main: '#25D366', dark: '#128C7E', lighter: '#DCF8C6' },
};

// Parse WhatsApp template markup to HTML
const parseWhatsappMarkup = (text) => {
  if (!text) return '';
  const tokens = text.split(/({{.*?}})/g);
  return tokens
    .map((token) => {
      if (token.startsWith('{{') && token.endsWith('}}')) {
        return `<span style="background:#E8F5E9;padding:2px 6px;border-radius:4px;color:#2E7D32;">${token}</span>`;
      }
      return token
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\*(.*?)\*/g, '<b>$1</b>')
        .replace(/_(.*?)_/g, '<i>$1</i>')
        .replace(/~(.*?)~/g, '<s>$1</s>')
        .replace(/```(.*?)```/gs, '<code>$1</code>')
        .replace(/\n/g, '<br>');
    })
    .join('');
};

// Get action-specific colors and icons
const getActionConfig = (actionType) => {
  switch (actionType) {
    case 'whatsapp':
      return {
        icon: 'mdi:whatsapp',
        color: colors.whatsapp.main,
        darkColor: colors.whatsapp.dark,
        lighterColor: colors.whatsapp.lighter,
        title: 'Send WhatsApp',
        sendLabel: 'Send WhatsApp',
      };
    case 'email':
      return {
        icon: 'mdi:email-fast',
        color: colors.info.main,
        darkColor: colors.info.dark,
        lighterColor: colors.info.lighter,
        title: 'Send Email',
        sendLabel: 'Send Email',
      };
    case 'reminder':
      return {
        icon: 'mdi:bell-ring',
        color: colors.warning.main,
        darkColor: colors.warning.dark,
        lighterColor: colors.warning.lighter,
        title: 'Add Follow-up Reminder',
        sendLabel: 'Add Reminder',
      };
    default:
      return {
        icon: 'mdi:send',
        color: colors.primary.main,
        darkColor: colors.primary.dark,
        lighterColor: colors.primary.lighter,
        title: 'Send Action',
        sendLabel: 'Send',
      };
  }
};

/**
 * AIActionPreviewModal - Preview and send WhatsApp/Email/Reminder from AI Summary
 * Shows template preview, allows template selection, and sends the action
 */
const AIActionPreviewModal = memo(({
  open,
  onClose,
  onSend,
  actionType = 'whatsapp', // 'whatsapp' | 'email' | 'reminder'
  aiRecommendation = {},
  leadInfo = {},
  isSending = false,
}) => {
  const config = getActionConfig(actionType);

  // Redux state
  const { whatsappAccountData } = useSelector((state) => state.whatsappAccount);
  const { wpTemplateListGlobal, loadingForWpTemplate } = useSelector((state) => state.whatsappMessages);
  const { practiceId } = useSelector((state) => state.practiceTreatmentList);
  const { templateList } = useSelector((state) => state.practiceMessage);

  // Local state
  const [searchQuery, setSearchQuery] = useState('');
  const [showTemplateList, setShowTemplateList] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [messageBody, setMessageBody] = useState('');
  const [reminderNote, setReminderNote] = useState('');
  const [reminderDateTime, setReminderDateTime] = useState(null);
  const [assignedTo, setAssignedTo] = useState([]);
  const connectedWhatsappAccount = getFirstConnectedWhatsappBusinessAccount(whatsappAccountData);

  // Get templates based on action type
  const templates = useMemo(() => {
    if (actionType === 'whatsapp') {
      return wpTemplateListGlobal || [];
    }
    if (actionType === 'email') {
      return templateList || [];
    }
    return [];
  }, [actionType, wpTemplateListGlobal, templateList]);

  // Load templates on open
  useEffect(() => {
    if (open && practiceId) {
      if (actionType === 'whatsapp' && connectedWhatsappAccount?.id) {
        dispatch(get10WpTemplateList({ wpAccountId: connectedWhatsappAccount.id }));
      }
      if (actionType === 'email') {
        dispatch(getTemplateList(practiceId));
      }
    }
  }, [open, practiceId, actionType, whatsappAccountData]);

  // Initialize with AI recommendation
  useEffect(() => {
    if (open && aiRecommendation) {
      if (actionType === 'reminder') {
        setReminderNote(aiRecommendation.reminderNote || '');
        // Parse reminder date if available
        if (aiRecommendation.reminderDate) {
          const parsedDate = moment(aiRecommendation.reminderDate, [
            'YYYY-MM-DD',
            'DD/MM/YYYY',
            'DD-MM-YYYY',
          ]);
          if (parsedDate.isValid()) {
            // Add time if specified
            if (aiRecommendation.reminderTime) {
              const [hours, minutes] = aiRecommendation.reminderTime.split(':');
              parsedDate.hours(parseInt(hours, 10) || 9);
              parsedDate.minutes(parseInt(minutes, 10) || 0);
            } else {
              parsedDate.hours(9);
              parsedDate.minutes(0);
            }
            setReminderDateTime(parsedDate.toDate());
          }
        }
      } else if (aiRecommendation.templateName) {
        // Try to find and select the AI-recommended template
        const matchedTemplate = templates.find(t =>
          t.name?.toLowerCase().includes(aiRecommendation.templateName.toLowerCase())
        );
        if (matchedTemplate) {
          handleSelectTemplate(matchedTemplate);
        }
      }
    }
  }, [open, aiRecommendation, actionType, templates]);

  // Handle template search
  const handleSearch = useCallback((value) => {
    setSearchQuery(value);
    if (value && actionType === 'whatsapp' && connectedWhatsappAccount?.id) {
      dispatch(getSearchWpTemplateList({
        wpAccountId: connectedWhatsappAccount.id,
        search: value,
      }));
    }
  }, [actionType, connectedWhatsappAccount]);

  // Handle template selection
  const handleSelectTemplate = useCallback((template) => {
    setSelectedTemplate(template);
    setShowTemplateList(false);
    setSearchQuery('');

    // Extract body from template
    if (actionType === 'whatsapp') {
      const bodyComponent = template.components?.find(c => c.type === 'BODY');
      setMessageBody(bodyComponent?.text || '');
    } else if (actionType === 'email') {
      setMessageBody(template.body || template.content || '');
    }
  }, [actionType]);

  // Send WhatsApp message via API
  const sendWhatsAppMessage = useCallback((templateName) => {
    if (!connectedWhatsappAccount?.id || !leadInfo?.phone) {
      console.error('Missing WhatsApp account or lead phone');
      onSend?.({ success: false, error: 'Missing WhatsApp account or phone number' });
      return;
    }

    dispatch(getWhatsappAccountPhones({
      practiceId,
      id: connectedWhatsappAccount.id,
      handleSuccess: (data) => {
        if (data?.success && data?.phoneId?.[0]?.id) {
          const messageData = {
            template_name: templateName,
            message_type: 'template',
            to: leadInfo.phone,
          };
          dispatch(whatsappMessageSend({
            practiceId,
            id: connectedWhatsappAccount.id,
            isBusinessAccountConnected: true,
            phoneId: data.phoneId[0].id,
            messageData,
            handleSuccess: (result) => {
              onSend?.({ success: result?.success, templateName, actionType: 'whatsapp' });
            },
            leadInfo: { id: leadInfo?.id, first_name: leadInfo?.first_name, last_name: leadInfo?.last_name },
          }));
        } else {
          onSend?.({ success: false, error: 'Could not get phone ID' });
        }
      },
    }));
  }, [connectedWhatsappAccount, practiceId, leadInfo, onSend]);

  // Handle send action
  const handleSend = useCallback(() => {
    if (actionType === 'reminder') {
      onSend?.({
        note: reminderNote,
        datetime: reminderDateTime ? `${moment(reminderDateTime).format('DD-MM-YYYY HH:mm:ss')}.0Z` : null,
        assignees: assignedTo.map(u => u.id),
        group: 'FOLLOW_UP',
        is_completed: false,
      });
    } else if (actionType === 'whatsapp' && selectedTemplate?.name) {
      // Actually send WhatsApp message via API
      sendWhatsAppMessage(selectedTemplate.name);
    } else {
      // For email or other actions, just pass data to parent
      onSend?.({
        templateName: selectedTemplate?.name || '',
        templateId: selectedTemplate?.id || '',
        messageBody,
        actionType,
      });
    }
  }, [actionType, selectedTemplate, messageBody, reminderNote, reminderDateTime, assignedTo, onSend, sendWhatsAppMessage]);

  // Check if send is disabled
  const isSendDisabled = useMemo(() => {
    if (isSending) return true;
    if (actionType === 'reminder') {
      return !reminderDateTime;
    }
    return !selectedTemplate && !messageBody;
  }, [isSending, actionType, selectedTemplate, messageBody, reminderDateTime]);

  const leadName = `${leadInfo?.first_name || ''} ${leadInfo?.last_name || ''}`.trim() || 'Patient';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'hidden',
          maxHeight: '90vh',
        },
      }}
      sx={{ zIndex: 1600 }}
    >
      {/* Header */}
      <Box sx={{
        background: actionType === 'whatsapp'
          ? `linear-gradient(135deg, ${colors.whatsapp.main} 0%, ${colors.whatsapp.dark} 100%)`
          : actionType === 'email'
            ? `linear-gradient(135deg, ${colors.info.main} 0%, ${colors.info.dark} 100%)`
            : `linear-gradient(135deg, ${colors.warning.main} 0%, ${colors.warning.dark} 100%)`,
        px: 2.5,
        py: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
      }}>
        <Box sx={{
          width: 44,
          height: 44,
          borderRadius: '12px',
          bgcolor: 'rgba(255,255,255,0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Iconify icon={config.icon} width={24} height={24} sx={{ color: '#fff' }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: '1.125rem', fontWeight: 700, color: '#fff' }}>
            {config.title}
          </Typography>
          <Typography sx={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.85)' }}>
            {actionType === 'reminder' ? 'Set a follow-up reminder' : `to ${leadName}`}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}>
          <Iconify icon="mdi:close" width={22} height={22} />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ p: 2.5, overflowY: 'auto', maxHeight: 'calc(90vh - 180px)' }}>
        {/* AI Recommendation Badge */}
        {aiRecommendation?.reason && (
          <Box sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            p: 1.5,
            mb: 2,
            bgcolor: colors.secondary.lighter,
            borderRadius: '10px',
            border: `1px solid ${colors.secondary.main}30`,
          }}>
            <Iconify icon="mdi:robot-happy" width={18} height={18} sx={{ color: colors.secondary.main, mt: 0.25 }} />
            <Box>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: colors.secondary.main, mb: 0.25 }}>
                AI Recommendation
              </Typography>
              <Typography sx={{ fontSize: '0.8125rem', color: colors.grey[700], fontStyle: 'italic' }}>
                {aiRecommendation.reason}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Reminder-specific fields */}
        {actionType === 'reminder' && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: colors.grey[700], mb: 0.75 }}>
                Reminder Date & Time (Required)
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  value={reminderDateTime}
                  onChange={setReminderDateTime}
                  format="dd/MM/yyyy HH:mm"
                  ampm={false}
                  disablePast
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      placeholder: 'Select date and time',
                    },
                    popper: {
                      sx: { zIndex: 1700 },
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: colors.grey[700], mb: 0.75 }}>
                Assign To
              </Typography>
              <UserSearchDropdown
                label=""
                assignToState={assignedTo}
                setAssignTo={setAssignedTo}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: colors.grey[700], mb: 0.75 }}>
                Reminder Note
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                size="small"
                value={reminderNote}
                onChange={(e) => setReminderNote(e.target.value)}
                placeholder="Add a note for this reminder..."
              />
            </Box>
          </>
        )}

        {/* Template selection for WhatsApp/Email */}
        {actionType !== 'reminder' && (
          <>
            {/* Template Search */}
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: colors.grey[700], mb: 0.75 }}>
                Select Template
              </Typography>
              <Box sx={{ position: 'relative' }}>
                <InputBase
                  fullWidth
                  placeholder={`Search ${actionType} templates...`}
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  onFocus={() => setShowTemplateList(true)}
                  startAdornment={
                    <Iconify icon="eva:search-outline" width={20} height={20} sx={{ color: colors.grey[500], mr: 1 }} />
                  }
                  sx={{
                    p: '10px 14px',
                    bgcolor: colors.grey[100],
                    borderRadius: '8px',
                    border: `1px solid ${colors.grey[200]}`,
                    '&:focus-within': {
                      borderColor: config.color,
                    },
                  }}
                />

                {/* Template List Dropdown */}
                {showTemplateList && (
                  <Box sx={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    mt: 0.5,
                    bgcolor: '#fff',
                    borderRadius: '8px',
                    border: `1px solid ${colors.grey[200]}`,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    maxHeight: 200,
                    overflow: 'auto',
                    zIndex: 10,
                  }}>
                    {loadingForWpTemplate ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : templates.length === 0 ? (
                      <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '0.8125rem', color: colors.grey[500] }}>
                          No templates found
                        </Typography>
                      </Box>
                    ) : (
                      templates.map((template, index) => (
                        <MenuItem
                          key={template.id || index}
                          onClick={() => handleSelectTemplate(template)}
                          sx={{
                            py: 1.25,
                            px: 2,
                            borderBottom: index < templates.length - 1 ? `1px solid ${colors.grey[100]}` : 'none',
                            '&:hover': { bgcolor: config.lighterColor },
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: colors.grey[700] }}>
                              {template.name || template.title || 'Untitled'}
                            </Typography>
                            {template.status && (
                              <Chip
                                label={template.status}
                                size="small"
                                sx={{
                                  mt: 0.5,
                                  height: 20,
                                  fontSize: '0.625rem',
                                  bgcolor: template.status === 'APPROVED' ? colors.success.lighter : colors.grey[200],
                                  color: template.status === 'APPROVED' ? colors.success.dark : colors.grey[700],
                                }}
                              />
                            )}
                          </Box>
                        </MenuItem>
                      ))
                    )}
                  </Box>
                )}
              </Box>
            </Box>

            {/* Selected Template Badge */}
            {selectedTemplate && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                mb: 2,
                bgcolor: config.lighterColor,
                borderRadius: '8px',
                border: `1px solid ${config.color}30`,
              }}>
                <Iconify icon="mdi:file-document-check" width={18} height={18} sx={{ color: config.darkColor }} />
                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: config.darkColor, flex: 1 }}>
                  {selectedTemplate.name || selectedTemplate.title}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedTemplate(null);
                    setMessageBody('');
                  }}
                  sx={{ p: 0.5 }}
                >
                  <Iconify icon="mdi:close" width={16} height={16} sx={{ color: config.darkColor }} />
                </IconButton>
              </Box>
            )}

            {/* Message Preview */}
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: colors.grey[700], mb: 0.75 }}>
                Message Preview
              </Typography>
              <Box sx={{
                p: 2,
                bgcolor: actionType === 'whatsapp' ? colors.whatsapp.lighter : colors.grey[100],
                borderRadius: '12px',
                border: `1px solid ${actionType === 'whatsapp' ? colors.whatsapp.main : colors.grey[200]}30`,
                minHeight: 120,
              }}>
                {messageBody ? (
                  <Typography
                    component="div"
                    sx={{ fontSize: '0.875rem', color: colors.grey[700], lineHeight: 1.6 }}
                    dangerouslySetInnerHTML={{ __html: parseWhatsappMarkup(messageBody) }}
                  />
                ) : (
                  <Typography sx={{ fontSize: '0.875rem', color: colors.grey[500], fontStyle: 'italic' }}>
                    Select a template to preview the message...
                  </Typography>
                )}
              </Box>
            </Box>
          </>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2.5,
        py: 2,
        borderTop: `1px solid ${colors.grey[200]}`,
        bgcolor: colors.grey[100],
      }}>
        <Button
          onClick={onClose}
          sx={{
            py: 1,
            px: 2.5,
            color: colors.grey[700],
            fontWeight: 600,
            borderRadius: '8px',
            textTransform: 'none',
          }}
        >
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={handleSend}
          disabled={isSendDisabled}
          startIcon={isSending ? (
            <CircularProgress size={16} sx={{ color: '#fff' }} />
          ) : (
            <Iconify icon={actionType === 'reminder' ? 'mdi:bell-plus' : 'mdi:send'} width={18} height={18} />
          )}
          sx={{
            py: 1,
            px: 2.5,
            bgcolor: config.color,
            color: '#fff',
            fontWeight: 700,
            borderRadius: '8px',
            textTransform: 'none',
            boxShadow: `0 4px 12px ${config.color}40`,
            '&:hover': { bgcolor: config.darkColor },
            '&:disabled': { bgcolor: colors.grey[300], boxShadow: 'none' },
          }}
        >
          {isSending ? 'Sending...' : config.sendLabel}
        </Button>
      </Box>
    </Dialog>
  );
});

AIActionPreviewModal.displayName = 'AIActionPreviewModal';

AIActionPreviewModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
  actionType: PropTypes.oneOf(['whatsapp', 'email', 'reminder']),
  aiRecommendation: PropTypes.shape({
    templateName: PropTypes.string,
    reason: PropTypes.string,
    reminderDate: PropTypes.string,
    reminderTime: PropTypes.string,
    reminderNote: PropTypes.string,
  }),
  leadInfo: PropTypes.object,
  isSending: PropTypes.bool,
};

export default AIActionPreviewModal;
